import { useEffect, useState } from "react";
import { api, getPlaybackUrl, getDistributionUrl } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Broadcast, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Outputs() {
  const [outputs, setOutputs] = useState(null);
  useEffect(() => { api.get("/outputs").then((r) => setOutputs(r.data)); }, []);

  const copyDistribution = (o) => {
    const u = getDistributionUrl(o.channel_id);
    navigator.clipboard.writeText(u)
      .then(() => toast.success("Distribution URL copied"))
      .catch(() => toast.error("Copy failed — clipboard blocked"));
  };
  const openLivePreview = async (o) => {
    try { const u = await getPlaybackUrl(o.channel_id); window.open(u, "_blank", "noreferrer"); }
    catch { toast.error("Live Preview is available once the channel is live"); }
  };

  return (
    <>
      <PageHeader title="Outputs" subtitle="Distribution endpoints · HLS manifests" />
      <div className="p-8 ccs-enter">
        {!outputs ? <Loading /> : outputs.length === 0 ? (
          <div className="text-center py-24 text-[#737373]">
            <Broadcast size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No outputs yet. Generate an HLS output from a channel page.</p>
          </div>
        ) : (
          <div className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden">
            <div className="grid grid-cols-12 px-5 py-2.5 border-b border-[#262626] text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">
              <div className="col-span-3">Output</div>
              <div className="col-span-2">Channel</div>
              <div className="col-span-2">Last Generated</div>
              <div className="col-span-5">Distribution URL (HLS)</div>
            </div>
            {outputs.map((o) => {
              return (
                <div key={o.id} data-testid={`output-row-${o.id}`} className="grid grid-cols-12 items-center px-5 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1F1F1F] transition-colors duration-150">
                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    <Broadcast size={16} className="text-emerald-500 shrink-0" />
                    <span className="text-sm truncate">{o.name}</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <StatusBadge status={o.channel_status} />
                  </div>
                  <div className="col-span-2 font-mono text-[11px] text-[#A3A3A3]" data-testid={`output-lastgen-${o.id}`}>{fmtDate(o.last_generated)}</div>
                  <div className="col-span-5 flex items-center gap-2">
                    <code data-testid={`distribution-url-${o.id}`} className="font-mono text-[10px] text-emerald-400 truncate flex-1">{getDistributionUrl(o.channel_id)}</code>
                    <button data-testid={`copy-distribution-${o.id}`} onClick={() => copyDistribution(o)} title="Copy Distribution URL"
                      className="text-[#737373] hover:text-white transition-colors duration-150"><Copy size={14} /></button>
                    <button data-testid={`open-live-preview-${o.id}`} onClick={() => openLivePreview(o)} title="Open Live Preview" className="text-[#737373] hover:text-[#3A86FF] transition-colors duration-150"><ArrowSquareOut size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
